import React from 'react'
import { Box, Typography, Button, CircularProgress, Fade, Chip } from '@mui/material'
import { CloudUpload, InsertDriveFile } from '@mui/icons-material'

type DocumentFormData = {
  agencyNameDocUrl: string
  agencyNameDocName: string
  dbaNameDocUrl: string
  dbaNameDocName: string
  einDocUrl: string
  einDocName: string
  w9FormUrl: string
  w9FormName: string
  npiOrganizationDocUrl: string
  npiOrganizationDocName: string
  certificateOfInsuranceUrl: string
  certificateOfInsuranceName: string
  clinicalAccreditationUrls?: string[]
  clinicalAccreditationNames?: string[]
}

type Props = {
  formData: DocumentFormData
  uploadingField: string | null
  onUpload: (fieldPrefix: string, file: File) => void
  onRemove?: (fieldPrefix: string, index: number) => void
  accentColor?: string
  errors?: Record<string, string>
}

const CORPORATE_DOCUMENTS: Array<{ fieldPrefix: string; label: string }> = [
  { fieldPrefix: 'agencyNameDoc', label: 'Secretary of State Statement of Information' },
  // { fieldPrefix: 'dbaNameDoc', label: 'Agency License' },
  { fieldPrefix: 'einDoc', label: 'EIN Document' },
  { fieldPrefix: 'npiOrganizationDoc', label: 'NPI Level II Certificate  (Organization)' },
  { fieldPrefix: 'w9Form', label: 'Signed W9 Form' },
  // { fieldPrefix: 'certificateOfInsurance', label: 'Certificate of Insurance' },
]

const AgencyDocumentsSection: React.FC<Props> = ({
  formData,
  uploadingField,
  onUpload,
  onRemove,
  accentColor = '#574F75',
  errors = {},
}) => {
  const handleFileChange =
    (fieldPrefix: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files
      if (files) {
        const fileList = Array.from(files)
        event.target.value = ''
        fileList.forEach(file => {
          onUpload(fieldPrefix, file)
        })
      }
    }

  const handleDrop = (fieldPrefix: string) => (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    const files = event.dataTransfer.files
    if (files) {
      Array.from(files).forEach(file => {
        onUpload(fieldPrefix, file)
      })
    }
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }

  return (
    <Box>
      {/* Corporate Documents */}
      <Fade in timeout={800} style={{ transitionDelay: '200ms' }}>
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            sx={{ color: 'grey.800', fontWeight: 600, fontSize: '1.125rem', mb: 2 }}
          >
            Corporate Documents
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {CORPORATE_DOCUMENTS.map(doc => {
              const fileUrl = (formData as unknown as Record<string, string>)[`${doc.fieldPrefix}Url`]
              const fileName = (formData as unknown as Record<string, string>)[`${doc.fieldPrefix}Name`]
              const isUploading = uploadingField === doc.fieldPrefix

              const errorKey = doc.fieldPrefix === 'w9Form' ? 'w9FormUrl' : `${doc.fieldPrefix}Url`
              const errorMsg = errors[errorKey] || errors[doc.fieldPrefix]

              return (
                <Box key={doc.fieldPrefix}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 2,
                      borderRadius: 2,
                      bgcolor: '#f8f9fa',
                      border: errorMsg ? '1px solid #d32f2f' : '1px solid #e9ecef',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                      <InsertDriveFile sx={{ color: 'grey.400' }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'grey.800' }}>
                          {doc.label}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: fileUrl ? 'grey.700' : 'grey.500',
                            fontSize: '0.8rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {fileName || 'No file chosen'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                      {fileUrl && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => window.open(fileUrl, '_blank')}
                          sx={{
                            textTransform: 'none',
                            borderColor: 'grey.300',
                            color: 'grey.700',
                            '&:hover': { borderColor: accentColor, color: accentColor },
                          }}
                        >
                          View
                        </Button>
                      )}
                      <Button
                        component="label"
                        variant="outlined"
                        size="small"
                        disabled={isUploading}
                        startIcon={
                          isUploading ? <CircularProgress size={14} /> : <CloudUpload fontSize="small" />
                        }
                        sx={{
                          textTransform: 'none',
                          borderColor: 'grey.300',
                          color: 'grey.700',
                          '&:hover': { borderColor: accentColor, color: accentColor },
                        }}
                      >
                        {fileUrl ? 'Replace' : 'Upload'}
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          hidden
                          onChange={handleFileChange(doc.fieldPrefix)}
                        />
                      </Button>
                    </Box>
                  </Box>
                  {errorMsg && (
                    <Typography
                      variant="caption"
                      sx={{ color: '#d32f2f', ml: 2, mt: 0.5, display: 'block' }}
                    >
                      {errorMsg}
                    </Typography>
                  )}
                </Box>
              )
            })}
          </Box>
        </Box>
      </Fade>

      {/* Clinical Accreditation (Optional) */}
      <Fade in timeout={800} style={{ transitionDelay: '300ms' }}>
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            sx={{ color: 'grey.800', fontWeight: 600, fontSize: '1.125rem', mb: 0.5 }}
          >
            Clinical Accreditation (Upload Multiple)
          </Typography>
          <Typography variant="body2" sx={{ color: 'grey.500', mb: 2 }}>
            Optional
          </Typography>
          <Box
            sx={{
              border: '2px dashed #d1d5db',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              bgcolor: 'white',
              cursor: uploadingField === 'clinicalAccreditation' ? 'wait' : 'pointer',
              opacity: uploadingField === 'clinicalAccreditation' ? 0.6 : 1,
              pointerEvents: uploadingField === 'clinicalAccreditation' ? 'none' : 'auto',
              '&:hover': {
                borderColor: accentColor,
                bgcolor: '#f8f9ff',
              },
            }}
            onDrop={handleDrop('clinicalAccreditation')}
            onDragOver={handleDragOver}
            onClick={() => document.getElementById('clinical-accreditation-upload')?.click()}
          >
            {uploadingField === 'clinicalAccreditation' ? (
              <CircularProgress size={40} sx={{ mb: 2, color: accentColor }} />
            ) : (
              <CloudUpload sx={{ fontSize: 40, color: '#9ca3af', mb: 2 }} />
            )}
            <Typography variant="body2" sx={{ color: 'grey.600', mb: 1, fontWeight: 500 }}>
              Drag and drop your accreditation documents, or click to browse
            </Typography>
            <Typography variant="caption" sx={{ color: 'grey.500', display: 'block', mb: 2 }}>
              Supported formats: PDF, JPG, PNG (Max 10MB)
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: accentColor,
                fontWeight: 500,
                textDecoration: 'underline',
                display: 'inline-block',
              }}
            >
              Browse
            </Typography>
            <input
              id="clinical-accreditation-upload"
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: 'none' }}
              onChange={handleFileChange('clinicalAccreditation')}
            />
          </Box>
          {formData.clinicalAccreditationUrls && formData.clinicalAccreditationUrls.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
              {formData.clinicalAccreditationUrls.map((url, index) => (
                <Chip
                  key={index}
                  label={formData.clinicalAccreditationNames?.[index] || 'Accreditation Document'}
                  size="small"
                  onDelete={(e) => {
                    e.stopPropagation()
                    onRemove?.('clinicalAccreditation', index)
                  }}
                  sx={{
                    maxWidth: 180,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    bgcolor: '#EAE7F2',
                    color: '#574F75',
                    fontWeight: 500,
                    border: '1px solid #D1C9E7',
                    '& .MuiChip-deleteIcon': {
                      color: '#7A6FA1',
                      '&:hover': {
                        color: '#574F75',
                      },
                    },
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Fade>
    </Box>
  )
}

export default AgencyDocumentsSection
